/**
 * Factory functions for creating input handlers
 */
import {
  handleNumberInputWithLimits,
  sanitizeNumericString,
  isInvalidNumericKey,
} from "../utils/textUtils";
import { updateTextAndSelectedText } from "../utils/stateUpdateUtils";

/**
 * Create handler for position/number inputs (X, Y, Width, StrokeWidth)
 */
export const createNumberInputHandler = ({
  field,
  min,
  max,
  textId,
  setTexts,
  setSelectedText,
  setLocalInputValues,
  saveToHistory,
  setPendingChanges,
  pendingChangesRef,
}) => {
  const markPending = () => {
    if (setPendingChanges) setPendingChanges(true);
    if (pendingChangesRef) pendingChangesRef.current = true;
  };

  return {
    onChange: (e) => {
      const rawValue = e.target.value;
      const value = sanitizeNumericString(rawValue);

      // If we stripped invalid characters, reflect the sanitized value in the input immediately
      if (value !== rawValue) {
        e.target.value = value;
      }

      const nativeEvent = e.nativeEvent || {};
      const inputType = nativeEvent.inputType;

      setLocalInputValues((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Allow arrow/spinner updates to propagate immediately, but defer plain typing until blur.
      const typingInputTypes = new Set([
        "insertText",
        "deleteContentBackward",
        "deleteContentForward",
        "insertCompositionText",
        "insertFromPaste",
        "insertFromDrop",
        "deleteByCut",
      ]);

      if (typingInputTypes.has(inputType)) {
        // Mark pending so that click-away/unselect will save (effect reads ref before state commits).
        markPending();
        return;
      }

      if (value === "") {
        return;
      }

      handleNumberInputWithLimits(
        field,
        value,
        min,
        max,
        (updatedField, updatedValue) => {
          // CRITICAL FIX: Ensure value respects min constraint (prevent negative values)
          const finalValue = Math.max(min, Math.min(max, updatedValue));
          updateTextAndSelectedText({
            textId,
            setTexts,
            setSelectedText,
            updates: { [updatedField]: finalValue },
          });
          markPending();
          setLocalInputValues((prev) => ({
            ...prev,
            [updatedField]: finalValue,
          }));
        }
      );
    },
    onBlur: (e) => {
      const rawVal = e.target.value;
      const val = sanitizeNumericString(rawVal);

      if (val !== rawVal) {
        e.target.value = val;
      }

      const defaultValue = val === "" ? min : parseInt(val, 10);

      handleNumberInputWithLimits(
        field,
        defaultValue,
        min,
        max,
        (field, value) => {
          // CRITICAL FIX: Ensure value respects min constraint (prevent negative values)
          const finalValue = Math.max(min, Math.min(max, value));
          updateTextAndSelectedText({
            textId,
            setTexts,
            setSelectedText,
            updates: { [field]: finalValue },
          });
          markPending();
          // Also update local input values to reflect the constrained value
          setLocalInputValues((prev) => ({
            ...prev,
            [field]: finalValue,
          }));
        }
      );
      if (saveToHistory) saveToHistory();
    },
    onKeyDown: (e) => {
      // Block invalid characters from ever appearing in the input
      if (isInvalidNumericKey(e.key)) {
        e.preventDefault();
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        e.target.blur();
      }
    },
  };
};
